import { queryKeys } from '../../../../../../queries/queryKeys';
import { createCompanyHousesKeys } from '../../../../../../queries/useCompanyHouses';

export const excludedRoomIds = [0, 1, 2];

export const getFilteredRooms = (rooms = []) =>
  rooms.filter((room) => !excludedRoomIds.includes(room.typeId));

export const stateTypes = {
  checkCompany: 'checkCompany',
  uncheckCompany: 'uncheckComapny',
  checkHouse: 'checkHouse',
  uncheckHouse: 'uncheckHouse',
  checkRoom: 'checkRoom',
  uncheckRoom: 'uncheckRoom',
  updateState: 'updateState',
  massCheckCompany: 'massCheckCompany',
  massUncheckCompany: 'massUncheckCompany',
  massCheckHouse: 'massCheckHouse',
  massUncheckHouse: 'massUncheckHouse',
  massCheckRoom: 'massCheckRoom',
  massUncheckRoom: 'massUncheckRoom',
};

const createHousesMap = (houses, houseId) => {
  return houses.reduce((acc, house) => {
    if (house.id !== houseId) {
      acc.set(house.id, new Set());
    }
    return acc;
  }, new Map());
};

const createRoomsSet = (rooms, roomId) =>
  rooms.reduce((acc, room) => {
    if (room.id !== roomId) {
      acc.add(room.id);
    }

    return acc;
  }, new Set());

const getFilteredRoomsSet = (rooms, selectedItems) => {
  const selectedIds = new Set(selectedItems.map((item) => item.id));

  return rooms.reduce((acc, room) => {
    if (!selectedIds.has(room.id)) {
      acc.add(room.id);
    }

    return acc;
  }, new Set());
};

const isCompanySelected = (state, companyId) =>
  state.get(companyId)?.size === 0;

const isHouseSelected = (state, companyId, houseId) =>
  state.get(companyId)?.get(houseId)?.size === 0;

const isAddressSelected = (state, companyId, houseId, addressId) =>
  Boolean(state.get(companyId)?.get(houseId)?.has(addressId));

const getRemovedBindIds = ({ state, initBinds }) => {
  const removedBindIds = new Set();

  initBinds.companyBinds.forEach((bind) => {
    if (!isCompanySelected(state, bind.serviceCompany.id)) {
      removedBindIds.add(bind.id);
    }
  });

  Array.from(state.entries()).forEach(([companyId, housesMap]) => {
    if (housesMap.size > 0) {
      const companyBind = initBinds.companyBinds.find(
        (bind) => bind.serviceCompany.id === companyId
      );

      if (companyBind) {
        removedBindIds.add(companyBind.id);
      }
    }
  });

  initBinds.houseBinds
    .flatMap((item) => item.houses)
    .forEach((bind) => {
      if (!isHouseSelected(state, bind.serviceCompany.id, bind.house.id)) {
        removedBindIds.add(bind.id);
      }
    });

  initBinds.roomBinds
    .flatMap((item) => item.houses)
    .flatMap((house) => house.rooms)
    .forEach((bind) => {
      if (
        !isAddressSelected(
          state,
          bind.serviceCompany.id,
          bind.address.houseId,
          bind.address.id
        )
      ) {
        removedBindIds.add(bind.id);
      }
    });

  return Array.from(removedBindIds);
};

export const stateReducer = (state, action) => {
  const companiesMap = new Map(state);

  switch (action.type) {
    case stateTypes.checkCompany: {
      companiesMap.set(action.companyId, new Map());
      return companiesMap;
    }

    case stateTypes.uncheckCompany: {
      companiesMap.delete(action.companyId);
      return companiesMap;
    }

    case stateTypes.checkHouse: {
      const { companyId, houseId, houseCount } = action;
      const housesMap = companiesMap.get(companyId) || new Map();

      housesMap.set(houseId, new Set());

      const isAllHouses =
        housesMap.size === houseCount &&
        Array.from(housesMap.values()).every((roomsSet) => roomsSet.size === 0);

      companiesMap.set(companyId, isAllHouses ? new Map() : housesMap);

      return companiesMap;
    }

    case stateTypes.uncheckHouse: {
      const { companyId, houseId, houses } = action;
      const housesMap = companiesMap.get(companyId);

      if (housesMap.size === 0) {
        houses.length === 1
          ? companiesMap.delete(companyId)
          : companiesMap.set(companyId, createHousesMap(houses, houseId));

        return companiesMap;
      }

      if (housesMap.size === 1) {
        companiesMap.delete(companyId);
        return companiesMap;
      }

      housesMap.delete(houseId);
      companiesMap.set(companyId, housesMap);
      return companiesMap;
    }

    case stateTypes.massCheckHouse: {
      const newState = new Map(state);
      const allHouses = action.houses || [];

      action.items.forEach((item) => {
        const companyId = item.serviceCompanyId;
        const houseId = item.id;

        const housesMap = new Map(newState.get(companyId) || []);

        housesMap.set(houseId, new Set());

        newState.set(companyId, housesMap);
      });

      allHouses.forEach((house) => {
        const companyId = house.serviceCompanyId;
        const housesMap = newState.get(companyId);

        if (!housesMap) return;

        const isAllHouses =
          housesMap.size === allHouses.length &&
          Array.from(housesMap.values()).every(
            (roomsSet) => roomsSet.size === 0
          );

        if (isAllHouses) {
          newState.set(companyId, new Map());
        }
      });

      return newState;
    }

    case stateTypes.massUncheckHouse: {
      const newState = new Map(state);

      action.items.forEach((item) => {
        const companyId = item.serviceCompanyId;
        const houseId = item.id;

        const currentHousesMap = newState.get(companyId);

        if (!currentHousesMap) return;

        const housesMap = new Map(
          Array.from(currentHousesMap.entries()).map(([id, roomIds]) => [
            id,
            new Set(roomIds),
          ])
        );

        if (currentHousesMap.size === 0) {
          const allHouses = action.houses;

          if (allHouses.length === 1) {
            newState.delete(companyId);
          } else {
            newState.set(companyId, createHousesMap(allHouses, houseId));
          }

          return;
        }

        if (housesMap.size === 1) {
          newState.delete(companyId);
          return;
        }

        const newHousesMap = new Map(housesMap);
        newHousesMap.delete(houseId);

        newState.set(companyId, newHousesMap);
      });

      return newState;
    }

    case stateTypes.checkRoom: {
      const { companyId, houseCount, roomCount, houseId, roomId } = action;

      const housesMap = companiesMap.get(companyId) || new Map();
      const roomsSet = housesMap.get(houseId) || new Set();

      roomsSet.add(roomId);

      housesMap.set(
        houseId,
        roomsSet.size === roomCount ? new Set() : roomsSet
      );

      const isAllHouses =
        housesMap.size === houseCount &&
        Array.from(housesMap.values()).every((roomsSet) => roomsSet.size === 0);

      companiesMap.set(companyId, isAllHouses ? new Map() : housesMap);

      return companiesMap;
    }

    case stateTypes.uncheckRoom: {
      const { companyId, houseId, roomId, houses, rooms } = action;

      const housesMap = companiesMap.get(companyId);
      const roomsSet = housesMap.get(houseId) || new Set();

      if (roomsSet.size === 0) {
        if (rooms.length === 1) {
          houses.length === 1
            ? companiesMap.delete(companyId)
            : companiesMap.set(companyId, createHousesMap(houses, houseId));

          return companiesMap;
        }

        if (housesMap.size === 0) {
          const newHousesMap = createHousesMap(houses, houseId);
          newHousesMap.set(houseId, createRoomsSet(rooms, roomId));
          companiesMap.set(companyId, newHousesMap);
          return companiesMap;
        }

        housesMap.set(houseId, createRoomsSet(rooms, roomId));
        companiesMap.set(companyId, housesMap);
        return companiesMap;
      }

      if (roomsSet.size === 1) {
        if (houses.length === 1 || housesMap.size === 1) {
          companiesMap.delete(companyId);
          return companiesMap;
        }

        housesMap.delete(houseId);
        companiesMap.set(companyId, housesMap);
        return companiesMap;
      }

      roomsSet.delete(roomId);
      housesMap.set(houseId, roomsSet);
      companiesMap.set(companyId, housesMap);
      return companiesMap;
    }

    case stateTypes.massCheckRoom: {
      const newState = new Map(state);
      const { companyId, houseCount, houseId, items, rooms } = action;

      const housesMap = new Map(newState.get(companyId) || []);
      const roomsSet = new Set(housesMap.get(houseId) || []);

      items.forEach((item) => {
        roomsSet.add(item.id);
      });

      housesMap.set(
        houseId,
        roomsSet.size === rooms.length ? new Set() : roomsSet
      );

      const isAllHouses =
        housesMap.size === houseCount &&
        Array.from(housesMap.values()).every((roomsSet) => roomsSet.size === 0);

      newState.set(companyId, isAllHouses ? new Map() : housesMap);

      return newState;
    }

    case stateTypes.massUncheckRoom: {
      const newState = new Map(state);
      const { companyId, houseId, houses, rooms, items } = action;

      const currentHousesMap = newState.get(companyId);

      if (!currentHousesMap) {
        return newState;
      }

      const housesMap = new Map(
        Array.from(currentHousesMap.entries()).map(([id, roomIds]) => [
          id,
          new Set(roomIds),
        ])
      );
      const roomsSet = housesMap.get(houseId);
      const unselectedIds = new Set(items.map((item) => item.id));

      if (currentHousesMap.size === 0) {
        const newHousesMap = createHousesMap(houses, houseId);
        const filteredRoomsSet = getFilteredRoomsSet(rooms, items);

        if (filteredRoomsSet.size > 0) {
          newHousesMap.set(houseId, filteredRoomsSet);
        }

        newHousesMap.size > 0
          ? newState.set(companyId, newHousesMap)
          : newState.delete(companyId);

        return newState;
      }

      if (!roomsSet) {
        return newState;
      }

      if (roomsSet.size === 0) {
        const filteredRoomsSet = getFilteredRoomsSet(rooms, items);

        if (filteredRoomsSet.size === 0) {
          housesMap.delete(houseId);
        } else {
          housesMap.set(houseId, filteredRoomsSet);
        }

        housesMap.size > 0
          ? newState.set(companyId, housesMap)
          : newState.delete(companyId);

        return newState;
      }

      const newRoomsSet = new Set(roomsSet);

      unselectedIds.forEach((roomId) => {
        newRoomsSet.delete(roomId);
      });

      if (newRoomsSet.size === 0) {
        housesMap.delete(houseId);
      } else {
        housesMap.set(houseId, newRoomsSet);
      }

      housesMap.size > 0
        ? newState.set(companyId, housesMap)
        : newState.delete(companyId);

      return newState;
    }

    case stateTypes.updateState: {
      return action.state;
    }
    default: {
      throw Error('Неизвестное действие: ' + action.type);
    }
  }
};

export const transformState = async ({
  state,
  queryClient,
  initBinds,
  prevBinds,
}) => {
  const serviceCompanyIds = [];
  const houseIds = [];
  let addressIds = [];

  const modalData = {
    companies: [],
    houses: [],
    addresses: [],
  };
  const removedBindIds = getRemovedBindIds({
    state,
    initBinds,
  });

  for (const [companyId, housesMap] of Array.from(state.entries())) {
    if (housesMap.size === 0) {
      if (!prevBinds.companies.has(companyId)) {
        serviceCompanyIds.push(companyId);
      }

      const companies = queryClient.getQueryData('companies');

      const companyData =
        companies?.length > 0
          ? companies.find((company) => company.id === companyId)
          : initBinds.companyBinds.find(
              (obj) => obj.serviceCompany.id === companyId
            );

      if (companyData) {
        modalData.companies.push(companyData);
      }

      continue;
    }

    for (const [houseId, roomsSet] of Array.from(housesMap.entries())) {
      const houses = queryClient.getQueryData(
        createCompanyHousesKeys(companyId)
      );

      const houseData =
        houses?.length > 0
          ? houses.find((house) => house.id === houseId)
          : initBinds.houseBinds
              .flatMap((item) => item.houses)
              .find((obj) => obj.house.id === houseId)?.house ||
            initBinds.roomBinds
              .flatMap((item) => item.houses)
              .find((house) => house.houseId === houseId);

      if (roomsSet.size === 0) {
        if (!prevBinds.actualHouses.has(houseId)) {
          houseIds.push(houseId);
        }

        if (houseData) {
          modalData.houses.push(houseData);
        }
      } else {
        const roomsSetClone = new Set(roomsSet);
        prevBinds.addresses.forEach((addressId) => {
          if (roomsSetClone.has(addressId)) {
            roomsSetClone.delete(addressId);
          }
        });

        addressIds = addressIds.concat([...roomsSetClone]);

        const addresses = queryClient.getQueryData(
          queryKeys.requestAddresses(houseId)
        );

        roomsSet.forEach((roomId) => {
          const addressData =
            addresses?.length > 0
              ? addresses.find((address) => address.id === roomId)
              : initBinds.roomBinds
                  .flatMap((item) => item.houses)
                  .flatMap((house) => house.rooms)
                  .find((room) => room.address.id === roomId)?.address;

          if (addressData) {
            modalData.addresses.push({ ...(houseData || {}), ...addressData });
          }
        });
      }
    }
  }

  const formValues = {
    ...(serviceCompanyIds.length > 0 ? { serviceCompanyIds } : {}),
    ...(houseIds.length > 0 ? { houseIds } : {}),
    ...(addressIds.length > 0
      ? { addressIds: Array.from(new Set(addressIds)) }
      : {}),
  };

  return {
    formValues,
    modalData,
    removedBindIds,
    duplicateHouses: [],
  };
};
